import { Fragment, useMemo, useState, type ReactNode } from 'react'
import ImageUpload from './ImageUpload'

interface Column {
  key: string
  label: string
  width?: string
  render?: (val: any, row: any) => ReactNode
}

interface GenericTableProps<T extends Record<string, any>> {
  title: string
  icon: string
  items: T[]
  columns: Column[]
  loading: boolean
  editingItem: Partial<T> | null
  hideAddButton?: boolean
  formFields: {
    key: keyof T
    label: string
    type?: 'text' | 'textarea' | 'select' | 'number' | 'image' | 'combo'
    options?: { value: string; label: string }[]
    required?: boolean
    disabled?: boolean
    folder?: string
    accept?: string
    allowPdf?: boolean
    placeholder?: string
    hint?: string
    cropAspectRatio?: number
    cropOutputWidth?: number
    cropOutputHeight?: number
  }[]
  groupBy?: {
    key: keyof T
    label?: (value: string) => string
  }
  onAdd: () => void
  onEdit: (item: T) => void
  onDelete: (id: number) => void
  onSave: (e: React.FormEvent) => void
  onCancel: () => void
  onFieldChange: (key: string, value: any) => void
}

export default function GenericTable<T extends Record<string, any>>({
  title, icon, items, columns, loading, editingItem, hideAddButton, formFields, groupBy,
  onAdd, onEdit, onDelete, onSave, onCancel, onFieldChange
}: GenericTableProps<T>) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  // Cluster rows that share the same groupBy key together (e.g. same Category
  // or same Department), tagging the first row of each cluster with a header.
  const groupedRows = useMemo(() => {
    if (!groupBy) {
      return items.map(item => ({ item, isGroupStart: false, groupLabel: '', groupCount: 0 }))
    }
    const order: string[] = []
    const buckets = new Map<string, T[]>()
    items.forEach(item => {
      const k = String((item as any)[groupBy.key] ?? '')
      if (!buckets.has(k)) { buckets.set(k, []); order.push(k) }
      buckets.get(k)!.push(item)
    })
    const rows: { item: T; isGroupStart: boolean; groupLabel: string; groupCount: number }[] = []
    order.forEach(k => {
      const bucket = buckets.get(k)!
      bucket.forEach((item, i) => {
        rows.push({
          item,
          isGroupStart: i === 0,
          groupLabel: groupBy.label ? groupBy.label(k) : k,
          groupCount: bucket.length
        })
      })
    })
    return rows
  }, [items, groupBy])

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: '14px',
    border: '1px solid #fed7aa',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(234,88,12,0.04)'
  }

  const thStyle: React.CSSProperties = {
    padding: '12px 14px',
    fontSize: '12px',
    fontWeight: 800,
    color: '#9a3412',
    textAlign: 'left',
    background: '#fff7ed',
    borderBottom: '1px solid #fed7aa',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  }

  const tdStyle: React.CSSProperties = {
    padding: '12px 14px',
    fontSize: '13.5px',
    color: '#292524',
    borderBottom: '1px solid #ffedd5',
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '9px 12px',
    borderRadius: '8px',
    border: '1.5px solid #fed7aa',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box'
  }

  const btnBase: React.CSSProperties = {
    padding: '7px 14px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer'
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800, color: '#1c1917' }}>
            {icon} {title}
          </h2>
          <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#78716c' }}>
            {items.length} records · ☁️ Database
          </p>
        </div>
        {!hideAddButton && (
          <button
            onClick={onAdd}
            style={{ ...btnBase, background: 'linear-gradient(135deg, #ea580c, #c2410c)', color: '#fff', padding: '9px 20px', boxShadow: '0 4px 12px rgba(234, 88, 12, 0.2)' }}
          >
            + Add New
          </button>
        )}
      </div>

      {/* Form */}
      {editingItem !== null && (
        <form onSubmit={onSave} style={{
          ...cardStyle,
          padding: '24px',
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #fff7ed, #ffedd5)',
          border: '1.5px solid #fdba74'
        }}>
          <h3 style={{ margin: '0 0 18px', fontSize: '16px', fontWeight: 800, color: '#9a3412' }}>
            {(editingItem as any).id ? '✏️ Edit Record' : '➕ New Record'}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {formFields.map((field, idx) => {
              const isTallField = field.type === 'textarea' || field.type === 'image'
              return (
                <div
                  key={String(field.key)}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: isTallField ? 'flex-start' : 'center',
                    gap: '8px 20px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    background: idx % 2 === 0 ? '#fff' : 'rgba(255,255,255,0.55)',
                    border: '1px solid #fde3c7'
                  }}
                >
                  <label style={{
                    width: '190px',
                    flex: '0 0 190px',
                    fontSize: '13px',
                    fontWeight: 800,
                    color: '#9a3412',
                    paddingTop: isTallField ? '9px' : 0
                  }}>
                    {field.label}{field.required && <span style={{ color: '#ef4444' }}> *</span>}
                  </label>
                  <div style={{ flex: '1 1 240px', minWidth: '220px' }}>
                    {field.type === 'image' ? (
                      <ImageUpload
                        value={(editingItem as any)[field.key] ?? ''}
                        onChange={(url) => onFieldChange(String(field.key), url)}
                        folder={field.folder ?? 'uploads'}
                        label={field.label}
                        accept={field.accept}
                        allowPdf={field.allowPdf}
                        cropAspectRatio={field.cropAspectRatio}
                        cropOutputWidth={field.cropOutputWidth}
                        cropOutputHeight={field.cropOutputHeight}
                      />
                    ) : field.type === 'textarea' ? (
                      <textarea
                        rows={3}
                        value={(editingItem as any)[field.key] ?? ''}
                        onChange={e => onFieldChange(String(field.key), e.target.value)}
                        disabled={field.disabled}
                        style={{ ...inputStyle, resize: 'vertical' }}
                      />
                    ) : field.type === 'select' ? (
                      <select
                        value={(editingItem as any)[field.key] ?? ''}
                        onChange={e => onFieldChange(String(field.key), e.target.value)}
                        style={{ ...inputStyle, background: '#fff' }}
                      >
                        {field.options?.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === 'combo' ? (
                      <>
                        <input
                          list={`dl-${String(field.key)}`}
                          type="text"
                          value={(editingItem as any)[field.key] ?? ''}
                          onChange={e => onFieldChange(String(field.key), e.target.value)}
                          disabled={field.disabled}
                          required={field.required}
                          placeholder={field.placeholder}
                          autoComplete="off"
                          style={{ ...inputStyle, background: field.disabled ? '#f1f5f9' : '#fff' }}
                        />
                        <datalist id={`dl-${String(field.key)}`}>
                          {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value} />
                          ))}
                        </datalist>
                      </>
                    ) : (
                      <input
                        type={field.type ?? 'text'}
                        value={(editingItem as any)[field.key] ?? ''}
                        onChange={e => onFieldChange(String(field.key), e.target.value)}
                        disabled={field.disabled}
                        required={field.required}
                        placeholder={field.placeholder}
                        style={{ ...inputStyle, background: field.disabled ? '#f1f5f9' : '#fff' }}
                      />
                    )}
                    {field.hint && (
                      <div style={{ fontSize: '11px', color: '#9a9490', marginTop: '4px' }}>{field.hint}</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button type="submit" style={{ ...btnBase, background: '#16a34a', color: '#fff' }}>
              💾 Save
            </button>
            <button type="button" onClick={onCancel} style={{ ...btnBase, background: '#64748b', color: '#fff' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <div style={cardStyle}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>
            Loading from D1 database...
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📭</div>
            No records found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '50px' }}>#</th>
                  {columns.map(col => (
                    <th key={col.key} style={{ ...thStyle, width: col.width }}>{col.label}</th>
                  ))}
                  <th style={{ ...thStyle, textAlign: 'right', width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let rowNumber = 0
                  return groupedRows.map((entry, idx) => {
                    const row = entry.item
                    rowNumber += 1
                    return (
                      <Fragment key={(row as any).id ?? `row-${idx}`}>
                        {groupBy && entry.isGroupStart && (
                          <tr>
                            <td
                              colSpan={columns.length + 2}
                              style={{
                                padding: '9px 14px',
                                background: 'linear-gradient(135deg, #fff7ed, #ffedd5)',
                                borderBottom: '1px solid #fed7aa',
                                borderTop: idx === 0 ? 'none' : '2px solid #fdba74',
                                fontWeight: 800,
                                fontSize: '12px',
                                color: '#9a3412',
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em'
                              }}
                            >
                              {entry.groupLabel || '—'}
                              <span style={{ marginLeft: '8px', fontWeight: 700, color: '#c2743f', textTransform: 'none', letterSpacing: 'normal' }}>
                                ({entry.groupCount})
                              </span>
                            </td>
                          </tr>
                        )}
                        <tr
                          style={{ transition: 'background 0.15s ease' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#fff7ed')}
                          onMouseLeave={e => (e.currentTarget.style.background = '')}
                        >
                          <td style={{ ...tdStyle, color: '#a8a29e', fontSize: '12px', fontWeight: 600 }}>{rowNumber}</td>
                          {columns.map(col => (
                            <td key={col.key} style={tdStyle} title={String((row as any)[col.key] ?? '')}>
                              {col.render ? col.render((row as any)[col.key], row) : String((row as any)[col.key] ?? '—')}
                            </td>
                          ))}
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <button
                              onClick={() => onEdit(row)}
                              style={{ ...btnBase, background: '#f97316', color: '#fff', padding: '5px 12px', marginRight: '6px', fontSize: '12px', boxShadow: '0 2px 4px rgba(249,115,22,0.2)' }}
                            >Edit</button>
                            {confirmDeleteId === (row as any).id ? (
                              <>
                                <button
                                  onClick={() => { onDelete((row as any).id!); setConfirmDeleteId(null) }}
                                  style={{ ...btnBase, background: '#ef4444', color: '#fff', padding: '5px 10px', fontSize: '12px', marginRight: '4px' }}
                                >Sure?</button>
                                <button
                                  onClick={() => setConfirmDeleteId(null)}
                                  style={{ ...btnBase, background: '#e5e7eb', color: '#374151', padding: '5px 8px', fontSize: '12px' }}
                                >✕</button>
                              </>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteId((row as any).id!)}
                                style={{ ...btnBase, background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', padding: '5px 10px', fontSize: '12px' }}
                              >Del</button>
                            )}
                          </td>
                        </tr>
                      </Fragment>
                    )
                  })
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
